<script setup lang="ts">
import { computed, watch, ref, onUpdated, onMounted } from 'vue';

const props = withDefaults(
  defineProps<{
    text: string;
    rows: number;
    title?: string;
  }>(),
  {
    rows: 1,
    title: '',
  }
);

const lineHeight = ref('1em');
const isMoreShown = ref(false);
const isHiddenMode = ref(true);

const el = ref<HTMLElement | null>(null);

watch(
  () => props.text,
  () => {
    isHiddenMode.value = true;
  }
);

onUpdated(() => {
  if (!el.value) return;

  isMoreShown.value = el.value.scrollHeight > el.value.clientHeight;
});

onMounted(() => {
  if (!el.value) return;
  lineHeight.value = getComputedStyle(el.value).lineHeight;
});

const maxHeight = computed(() => {
  return isHiddenMode.value ? `calc(${props.rows} * ${lineHeight.value})` : 'none';
});

// ВАЖНО: Форматируем текст для характеристик
// В legacy проекте в props текста приходил уже с переносами строк (возможно обрабатывался в мапперах).
// В новом проекте текст приходит в одну строку (например: "черный, красный, синий"),
// поэтому мы форматируем его здесь на фронте, заменяя ", " на ",\n".
// Если возникают проблемы с отображением текста - обратите внимание на эту функцию форматирования.
const formattedText = computed(() => {
  if (!props.text) return '';
  // Заменяем запятые с пробелами на переносы строк
  return props.text.replace(/,\s+/g, ',\n');
});

const onShowMoreClick = () => {
  isMoreShown.value = false;
  isHiddenMode.value = false;
};
</script>

<template>
  <div ref="el" class="disclosure" :style="{ maxHeight }">
    <span>{{ formattedText }}</span>
    <div v-if="isMoreShown" @click="onShowMoreClick" class="disclosure__more">ещё</div>
  </div>
</template>

<style lang="stylus" scoped>
.disclosure
  width fit-content
  height fit-content
  overflow hidden
  position relative
  white-space pre-line
  word-wrap break-word

  span
    white-space pre-line
    word-wrap break-word

  &__more
    position absolute
    bottom 0
    right 0
    color #8B8E99
    background-color #fff
    cursor pointer
    line-height inherit
    display flex
    align-items center
    padding-left 4px

    &:before
      position absolute
      content ''
      display inline-block
      width 1.5em
      height 1em
      background: linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 90%, rgba(255,255,255,1) 100%);
      left -1.5em
</style>
